import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { Select, Tooltip, Typography } from 'antd';
import { PlusOutlined, SettingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useStore } from '../../../../../../../../store/rootStore';
import { useStore as useBotAdminStore } from '../../../../../../../../store/BotAdminStore';
import { getDirection } from '../../../../../../../../helpers';
import CreateNewMiniBot from '../../../../../../../BotAdmin/Modals/CreateNewMiniBot';
import styles from './BotSelector.module.scss';
import { MySelect } from '../../../../../../../Base/Form';
const { Text } = Typography;
const { Option } = Select;
const BotSelector = observer(({ templateId, templateName, channelId, value, onChange, }) => {
    var _a, _b, _c;
    const { t } = useTranslation();
    const history = useHistory();
    const rootStore = useStore();
    const botAdminStore = useBotAdminStore();
    const [hoveredBot, setHoveredBot] = useState(null);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    useEffect(() => {
        if (botAdminStore.bots.size === 0) {
            botAdminStore.loadBots();
        }
    }, []);
    const allBots = Array.from(botAdminStore.bots.values());
    const channelDefaultBot = channelId
        ? (_b = (_a = rootStore.channels.getAccount(channelId)) === null || _a === void 0 ? void 0 : _a.bot) !== null && _b !== void 0 ? _b : null
        : null;
    // Get current template object for pre-filling the modal
    const currentTemplate = (() => {
        if (!(channelId === null || channelId === void 0 ? void 0 : channelId.id) || !templateId)
            return null;
        const templates = rootStore.templateMessagesStore.templateMessagesLists[channelId.id];
        return (templates === null || templates === void 0 ? void 0 : templates.find(t => t._id === templateId)) || null;
    })();
    const handleSelectBot = (botIdentifier) => {
        if (botIdentifier === 'new-mini-bot') {
            handleCreateNewMiniBot();
            return;
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(botIdentifier || null);
    };
    const handleCreateNewMiniBot = () => {
        setCreateModalVisible(true);
    };
    const handleCreateModalCancel = () => {
        setCreateModalVisible(false);
    };
    const handleCreateModalConfirm = (identifier, modalChannelId, template) => {
        setCreateModalVisible(false);
        history.push('/bot-admin/minibot-builder', {
            identifier,
            channelId: modalChannelId ? { id: modalChannelId.id, name: modalChannelId.name } : null,
            templateId: (template === null || template === void 0 ? void 0 : template._id) || null,
        });
    };
    const handleEditBot = (e, botIdentifier) => {
        e.stopPropagation();
        history.push('/bot-admin/minibot-builder', {
            identifier: botIdentifier,
            templateId,
            templateName,
            channelId: channelId ? { id: channelId.id, name: channelId.name } : null,
        });
    };
    const isLoading = botAdminStore.botsLoading;
    return (React.createElement("div", { className: styles.container },
        React.createElement("label", { className: styles.label },
            React.createElement(Trans, { i18nKey: "selectBot" }),
            React.createElement(Tooltip, { title: React.createElement(Trans, { i18nKey: "selectBotHelp" }), placement: getDirection() === 'rtl' ? 'bottomLeft' : 'bottomRight', arrowPointAtCenter: true },
                React.createElement(QuestionCircleOutlined, { className: styles.labelIcon }))),
        React.createElement(MySelect, { className: styles.select, value: value || undefined, onChange: handleSelectBot, placeholder: t('notSelected'), loading: isLoading, dropdownRender: (menu) => (React.createElement(React.Fragment, null,
                menu,
                React.createElement("div", { className: styles.dropdownFooter },
                    React.createElement("div", { className: styles.newMiniBotButton, onClick: handleCreateNewMiniBot },
                        React.createElement(PlusOutlined, null),
                        React.createElement("span", null,
                            React.createElement(Trans, { i18nKey: "newMiniBot" })))))), notFoundContent: React.createElement("div", { className: styles.emptyState },
                React.createElement(Text, { type: "secondary" },
                    React.createElement(Trans, { i18nKey: "noBotsYet" }))) },
            channelDefaultBot && (React.createElement(Option, { key: channelDefaultBot, value: channelDefaultBot, className: styles.botOption },
                React.createElement("div", { className: styles.botOptionContent },
                    React.createElement("span", { className: styles.botName },
                        ((_c = botAdminStore.bots.get(channelDefaultBot)) === null || _c === void 0 ? void 0 : _c.title) || channelDefaultBot,
                        React.createElement(Text, { type: "secondary", className: styles.botNameLabel },
                            "(",
                            React.createElement(Trans, { i18nKey: "channelDefault" }),
                            ")"))))),
            allBots
                .filter(bot => bot.identifier !== channelDefaultBot)
                .map((bot) => (React.createElement(Option, { key: bot.identifier, value: bot.identifier, className: styles.botOption },
                React.createElement("div", { className: styles.botOptionContent, onMouseEnter: () => setHoveredBot(bot.identifier), onMouseLeave: () => setHoveredBot(null) },
                    React.createElement("span", { className: styles.botName }, bot.title || bot.identifier),
                    hoveredBot === bot.identifier && (React.createElement(Tooltip, { title: React.createElement(Trans, { i18nKey: "editBot" }) },
                        React.createElement(SettingOutlined, { className: styles.settingsIcon, onClick: (e) => handleEditBot(e, bot.identifier) })))))))),
        React.createElement(CreateNewMiniBot, { visible: createModalVisible, onCancel: handleCreateModalCancel, onConfirm: handleCreateModalConfirm, initialChannel: channelId, initialTemplate: currentTemplate })));
});
export default BotSelector;
